import _ from 'lodash'
import RequestError from './RequestError'
import api from '@/api'

/**
 * Site management from the dashboard. Everything goes through castr:
 *  - `/sites/:id/admin/<path>`  → runtime headless site-admin API
 *    (internal/sites-admin-api.md v0.2), castr adds the service token
 *  - `/sites/:id/plans*`        → paywall site products (castr holds the key)
 *  - `/sites/:id/viewers*`      → paywall viewers (owner audience)
 *  - `/sites/:id/castr-playback`→ castr resolves a Castr stream/VOD/playlist
 * The browser never talks to the runtime or the paywall directly.
 */
export default {
	// runtime site-admin
	getSiteInfo,
	listContent,
	createContent,
	updateContent,
	bulkUpdateContent,
	deleteContent,
	reorderContent,
	listCategories,
	createCategory,
	updateCategory,
	deleteCategory,
	reorderCategories,
	listFeatured,
	createFeatured,
	updateFeatured,
	deleteFeatured,
	reorderFeatured,
	listPages,
	createPage,
	updatePage,
	deletePage,
	reorderPages,
	getSettings,
	updateSettings,
	publishDraft,
	discardDraft,
	uploadImage,
	listEvents,
	createEvent,
	updateEvent,
	deleteEvent,
	// castr-owned (publish itself lives in SitesService / the Sites store)
	createPreview,
	getMonetization,
	listTransactions,
	getTransactionsSummary,
	refundTransaction,
	downloadTransactionsCsv,
	resumeMonetization,
	resolveCastrPlayback,
	listPlans,
	createPlan,
	updatePlan,
	replacePlanItems,
	archivePlan,
	listViewers,
	downloadViewersCsv,
	getViewer,
	updateViewer,
	sendViewerPasswordReset,
	lockViewer,
	unlockViewer,
	grantViewerAccess,
	revokeViewerGrant,
	listPromos,
	createPromo,
	updatePromo,
	deletePromo,
	listPromoRedemptions,
	listDomains,
	addDomain,
	verifyDomain,
	removeDomain,
	setPrimaryDomain,
	getGiving,
	updateGiving,
	getGivingSummary,
	listDonations,
	refundDonation,
}

const admin = (siteId, path) => `/sites/${siteId}/admin/${path}`

function getSiteInfo(siteId) {
	return makeRequest(admin(siteId, 'site'))
}

// --- content ---------------------------------------------------------------
function listContent(siteId, params = {}) {
	return makeRequest({ path: admin(siteId, 'content'), params: { limit: 500, ...params } })
}
function createContent(siteId, item) {
	return makeRequest({ method: 'post', path: admin(siteId, 'content'), data: item })
}
function updateContent(siteId, id, patch) {
	return makeRequest({ method: 'patch', path: admin(siteId, `content/${id}`), data: patch })
}
/**
 * One change for many items (runtime `POST /content/bulk`, sites-admin-api.md
 * v0.10). Callers fall back to per-item PATCH when the runtime does not have
 * the route yet — see `isBulkUnsupported`.
 * @param {{ ids: number[], access?: string, planIds?: string[], hidden?: boolean, categoryIds?: number[] }} body
 * @return {Promise<{ items?: object[], updated?: number }>}
 */
function bulkUpdateContent(siteId, body) {
	return makeRequest({ method: 'post', path: admin(siteId, 'content/bulk'), data: body })
}
function deleteContent(siteId, id) {
	return makeRequest({ method: 'delete', path: admin(siteId, `content/${id}`) })
}
function reorderContent(siteId, ids) {
	return makeRequest({ method: 'post', path: admin(siteId, 'content/reorder'), data: { ids } })
}

// --- categories ------------------------------------------------------------
function listCategories(siteId) {
	return makeRequest({ path: admin(siteId, 'categories'), params: { limit: 500 } })
}
function createCategory(siteId, category) {
	return makeRequest({ method: 'post', path: admin(siteId, 'categories'), data: category })
}
function updateCategory(siteId, id, patch) {
	return makeRequest({ method: 'patch', path: admin(siteId, `categories/${id}`), data: patch })
}
function deleteCategory(siteId, id) {
	return makeRequest({ method: 'delete', path: admin(siteId, `categories/${id}`) })
}
function reorderCategories(siteId, ids) {
	return makeRequest({ method: 'post', path: admin(siteId, 'categories/reorder'), data: { ids } })
}

// --- featured / hero -------------------------------------------------------
function listFeatured(siteId) {
	return makeRequest({ path: admin(siteId, 'featured'), params: { limit: 500 } })
}
function createFeatured(siteId, item) {
	return makeRequest({ method: 'post', path: admin(siteId, 'featured'), data: item })
}
function updateFeatured(siteId, id, patch) {
	return makeRequest({ method: 'patch', path: admin(siteId, `featured/${id}`), data: patch })
}
function deleteFeatured(siteId, id) {
	return makeRequest({ method: 'delete', path: admin(siteId, `featured/${id}`) })
}
function reorderFeatured(siteId, ids) {
	return makeRequest({ method: 'post', path: admin(siteId, 'featured/reorder'), data: { ids } })
}

// --- pages -----------------------------------------------------------------
function listPages(siteId) {
	return makeRequest({ path: admin(siteId, 'pages'), params: { limit: 500 } })
}
function createPage(siteId, page) {
	return makeRequest({ method: 'post', path: admin(siteId, 'pages'), data: page })
}
function updatePage(siteId, id, patch) {
	return makeRequest({ method: 'patch', path: admin(siteId, `pages/${id}`), data: patch })
}
function deletePage(siteId, id) {
	return makeRequest({ method: 'delete', path: admin(siteId, `pages/${id}`) })
}
function reorderPages(siteId, ids) {
	return makeRequest({ method: 'post', path: admin(siteId, 'pages/reorder'), data: { ids } })
}

// --- settings / uploads ----------------------------------------------------
// Draft / publish (internal/sites-draft-publish-contract.md §2): GET answers
// the published values plus `draft` (keys that differ), `draftCount` and
// `draftUpdatedAt`; a PATCH with `target=draft` writes the draft only. A
// runtime without draft support ignores the flag and publishes immediately —
// callers tell the two apart by `draftCount` being a number on the answer.
/** @return {Promise<{ settings: object, seeded?: string[], writableKeys?: string[], draft?: object, draftCount?: number, draftUpdatedAt?: string|null }>} */
function getSettings(siteId) {
	return makeRequest(admin(siteId, 'settings'))
}
/** @param {{ target?: 'draft' }} [opts] */
function updateSettings(siteId, patch, { target } = {}) {
	return makeRequest({ method: 'patch', path: admin(siteId, 'settings'), params: target ? { target } : undefined, data: patch })
}
/** Runtime-side publish of the settings draft (castr's `POST /sites/{id}/publish` calls this itself). */
function publishDraft(siteId) {
	return makeRequest({ method: 'post', path: admin(siteId, 'publish'), data: {} })
}
/** @return {Promise<{ discarded: number }>} */
function discardDraft(siteId) {
	return makeRequest({ method: 'delete', path: admin(siteId, 'draft') })
}
/**
 * Direct-to-R2 upload through the proxy (multipart, field `file`).
 * @return {Promise<{ url: string, absoluteUrl: string, contentType: string, size: number }>}
 */
function uploadImage(siteId, file) {
	const form = new FormData()
	form.append('file', file)
	return makeRequest({
		method: 'post',
		path: admin(siteId, 'uploads'),
		data: form,
		headers: { 'Content-Type': 'multipart/form-data' },
	})
}

// --- calendar events (runtime `events`, shown on the site's /calendar) ------
// internal/sites-admin-api.md §10: { id, contentId, title, start, end, content }.
/**
 * @param {{ from?: string, to?: string, contentId?: number, limit?: number, offset?: number }} [params] ISO range
 * @return {Promise<{ items: object[], total: number }>}
 */
function listEvents(siteId, params = {}) {
	return makeRequest({ path: admin(siteId, 'events'), params: { limit: 500, ...params } })
}
/** @param {{ contentId: number, title?: string|null, start: string, end?: string|null }} event */
function createEvent(siteId, event) {
	return makeRequest({ method: 'post', path: admin(siteId, 'events'), data: event })
}
function updateEvent(siteId, id, patch) {
	return makeRequest({ method: 'patch', path: admin(siteId, `events/${id}`), data: patch })
}
function deleteEvent(siteId, id) {
	return makeRequest({ method: 'delete', path: admin(siteId, `events/${id}`) })
}

// --- castr-owned -----------------------------------------------------------
/**
 * @param {{ type: 'live'|'vod'|'playlist', id: string, fileId?: string }} castrRef
 */
function resolveCastrPlayback(siteId, castrRef) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/castr-playback`, data: { castr_ref: castrRef } })
}

// --- preview (castr, control-plane §6g) ------------------------------------
/**
 * Preview session: a one-hour link that shows the site with its unpublished
 * changes, even while the site is `draft` / offline. 503
 * `reason: 'sites_admin_unconfigured'` without a signing key.
 * @return {Promise<{ url: string, expires_at: string, host: string }>}
 */
function createPreview(siteId) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/preview`, data: {} })
}

/**
 * Stripe Connect state of the site's paywall tenant + pause state of its
 * paid plans (castr GET /sites/:id/monetization).
 */
function getMonetization(siteId) {
	return makeRequest(`/sites/${siteId}/monetization`)
}
/** Re-activate the plans castr paused when Stripe was unlinked. */
function resumeMonetization(siteId) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/monetization/resume`, data: {} })
}

// --- transactions & revenue (castr walks the paywall; control-plane §6e) ----
// 503 `reason: 'paywall_site_unconfigured'` until the site is linked to the
// owner's Paywall account. `summary` always covers the whole site.
/**
 * `from` / `to` are ISO dates (inclusive day bounds); the `summary` answers
 * `series[]` (daily totals) and `recent[]` (newest purchases) on newer castr.
 * @param {{ page?: number, per_page?: number, status?: string, q?: string, from?: string, to?: string, refresh?: 1 }} [params]
 * @return {Promise<{ data: object[], meta: { current_page: number, last_page: number, per_page: number, total: number }, summary: object }>}
 */
function listTransactions(siteId, params = {}) {
	return makeRequest({ path: `/sites/${siteId}/transactions`, params })
}
/**
 * CSV of the transactions matching the same filters (castr `GET …/transactions.csv`).
 * The dashboard authenticates with a header, so the file is fetched and saved
 * client-side instead of linked.
 * @return {Promise<Blob>}
 */
function downloadTransactionsCsv(siteId, params = {}) {
	return makeRequest({ path: `/sites/${siteId}/transactions.csv`, params, responseType: 'blob' })
}
/**
 * `from` / `to` (`YYYY-MM-DD`, inclusive UTC days) scope the summary, its
 * `series` and `recent`; without them it covers the site's lifetime.
 * @param {{ refresh?: boolean, from?: string, to?: string }} [opts]
 * @return {Promise<{ summary: object, range?: { from: string, to: string }|null }>}
 */
function getTransactionsSummary(siteId, { refresh = false, from, to } = {}) {
	const params = {}
	if (refresh) params.refresh = 1
	if (from) params.from = from
	if (to) params.to = to
	return makeRequest({ path: `/sites/${siteId}/transactions/summary`, params: Object.keys(params).length ? params : undefined })
}
/**
 * Full refund of one payment (the newest paid one unless `paymentId` is given).
 * Partial amounts → 422 `partial_refund_unsupported`; nothing left → 409 `nothing_to_refund`.
 * @param {{ paymentId?: number, amount?: number, reason?: string }} [body]
 * @return {Promise<{ transaction: object, refund: { payment_id: number, amount: number, currency: string }, summary: object }>}
 */
function refundTransaction(siteId, invoiceId, body = {}) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/transactions/${encodeURIComponent(invoiceId)}/refund`, data: body })
}

function listPlans(siteId) {
	return makeRequest(`/sites/${siteId}/plans`)
}
function createPlan(siteId, plan) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/plans`, data: plan })
}
function updatePlan(siteId, productId, patch) {
	return makeRequest({ method: 'put', path: `/sites/${siteId}/plans/${productId}`, data: patch })
}
function replacePlanItems(siteId, productId, items) {
	return makeRequest({ method: 'put', path: `/sites/${siteId}/plans/${productId}/items`, data: { items } })
}
function archivePlan(siteId, productId) {
	return makeRequest({ method: 'delete', path: `/sites/${siteId}/plans/${productId}` })
}

/**
 * Rows carry `plan`, `total_spent`, `last_seen`, `status` on newer castr;
 * filters `status=active|locked`, `plan=<public_id>`, `site_only=1` likewise.
 * `locked=1` stays the documented filter for deactivated viewers.
 * @param {{ q?: string, page?: number, per_page?: number, locked?: 1, status?: string, plan?: string, site_only?: 1 }} [params]
 */
function listViewers(siteId, params = {}) {
	return makeRequest({ path: `/sites/${siteId}/viewers`, params })
}
/** @return {Promise<Blob>} */
function downloadViewersCsv(siteId, params = {}) {
	return makeRequest({ path: `/sites/${siteId}/viewers.csv`, params, responseType: 'blob' })
}
function getViewer(siteId, viewerId) {
	return makeRequest(`/sites/${siteId}/viewers/${viewerId}`)
}
function updateViewer(siteId, viewerId, patch) {
	return makeRequest({ method: 'patch', path: `/sites/${siteId}/viewers/${viewerId}`, data: patch })
}
function sendViewerPasswordReset(siteId, viewerId) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/viewers/${viewerId}/password-reset` })
}
/**
 * Owner-wide lock (the viewer cannot sign in on any of the owner's sites).
 * @param {{ reason?: string, cancel_subscriptions?: boolean }} payload `cancel_subscriptions`
 *   cancels the viewer's active recurring purchases on this site (default false).
 * @return {Promise<{ user: object, locked: true, lock: object, cancelled_subscriptions?: string[] }>}
 */
function lockViewer(siteId, viewerId, payload = {}) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/viewers/${viewerId}/lock`, data: payload })
}
function unlockViewer(siteId, viewerId) {
	return makeRequest({ method: 'delete', path: `/sites/${siteId}/viewers/${viewerId}/lock` })
}
/**
 * @param {{ product?: string, items?: string[], expires_at?: string, reason?: string }} grant
 */
function grantViewerAccess(siteId, viewerId, grant) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/viewers/${viewerId}/grants`, data: grant })
}
function revokeViewerGrant(siteId, viewerId, grantId) {
	return makeRequest({ method: 'delete', path: `/sites/${siteId}/viewers/${viewerId}/grants/${grantId}` })
}

// --- promo codes (castr → paywall, sites-control-plane-api.md §6f) ---------
/** @return {Promise<{ data: object[], meta: { current_page: number, per_page: number, total: number, last_page: number } }>} */
function listPromos(siteId, params = {}) {
	return makeRequest({ path: `/sites/${siteId}/promos`, params })
}
/**
 * @param {{ code: string, type: 'percent'|'fixed'|'comp', value?: number, currency?: string, products: string[]|'all', max_redemptions?: number|null, expires_at?: string|null }} promo
 */
function createPromo(siteId, promo) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/promos`, data: promo })
}
/** @param {{ active?: boolean, max_redemptions?: number|null, expires_at?: string|null }} patch */
function updatePromo(siteId, promoId, patch) {
	return makeRequest({ method: 'patch', path: `/sites/${siteId}/promos/${promoId}`, data: patch })
}
function deletePromo(siteId, promoId) {
	return makeRequest({ method: 'delete', path: `/sites/${siteId}/promos/${promoId}` })
}
function listPromoRedemptions(siteId, promoId, params = {}) {
	return makeRequest({ path: `/sites/${siteId}/promos/${promoId}/redemptions`, params })
}

// --- custom domains (castr → Cloudflare for SaaS) ---------------------------
// internal/sites-control-plane-api.md §6d. Every call answers 503 with
// `reason: 'custom_domains_unconfigured'` until the zone/token exist on castr.
// Without `sites:custom-domain` on the plan, add / verify / custom primary
// answer 403 `reason: 'custom_domains_not_in_plan'` and the list says `allowed: false`.
// `max` is the plan's `sites:max-custom-domains` (per site); an add beyond it
// answers 403 `reason: 'custom_domains_limit'`.
/**
 * @param {{ refresh?: boolean }} [opts] `refresh` re-reads pending domains from Cloudflare
 * @return {Promise<{ configured: boolean, allowed?: boolean, cnameTarget: string, max: number, used: number, primaryHost: string, canonicalHost: string|null, domains: object[] }>}
 */
function listDomains(siteId, { refresh = false } = {}) {
	return makeRequest({ path: `/sites/${siteId}/domains`, params: refresh ? { refresh: 1 } : undefined })
}
/** @return {Promise<{ domain: object, site: object }>} 201 */
function addDomain(siteId, hostname) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/domains`, data: { hostname } })
}
/** @return {Promise<{ domain: object, activated: boolean, site: object }>} */
function verifyDomain(siteId, hostname) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/domains/${encodeURIComponent(hostname)}/verify` })
}
/** @return {Promise<{ success: boolean, cloudflareCleanup: 'ok'|'failed'|'skipped', site: object }>} */
function removeDomain(siteId, hostname) {
	return makeRequest({ method: 'delete', path: `/sites/${siteId}/domains/${encodeURIComponent(hostname)}` })
}
/** Pass the Castr address (`site.primaryHost`) to revert to it. @return {Promise<{ canonicalHost: string, site: object }>} */
function setPrimaryDomain(siteId, hostname) {
	return makeRequest({ method: 'put', path: `/sites/${siteId}/domains/${encodeURIComponent(hostname)}/primary` })
}

// --- giving (castr orchestrates runtime settings + paywall donation product) --
// internal/sites-broadcast-template-contract.md §5. The presentation keys
// (`givingMode`, `givingButtonLabel`, `givingThankYouMessage`,
// `broadcastGivingMessage`) are runtime settings;
// amounts live on the paywall donation product (`native`). 503
// `sites_admin_unconfigured` / `paywall_site_unconfigured` like viewers.
/**
 * @return {Promise<{ mode: 'off'|'native', buttonLabel: string, thankYouMessage: string, givingMessage: string|null,
 *   native: { productId: string, presets: number[], minAmount: number, currency: string, customAmountEnabled: boolean, active: boolean }|null,
 *   nativeError: string|null, paywallDonationsEnabled: boolean|null,
 *   nativeUnavailable: 'donations_disabled'|'paywall_donations_disabled'|'paywall_unavailable'|null,
 *   draftKeys: string[], writable: boolean, template: string|null, caps: { donations: boolean } }>}
 *   `caps.donations` is false only while castr's SITES_DONATIONS_ENABLED kill switch is pulled; the
 *   dashboard offers native giving regardless and shows `nativeUnavailable` as a notice.
 */
function getGiving(siteId) {
	return makeRequest(`/sites/${siteId}/giving`)
}
/**
 * One save for the Giving card. `409 donations_disabled` when native giving
 * is off on castr, `400 stripe_inactive` when the owner's Stripe account
 * cannot take payments, `502 runtime_settings_failed` (`partial: true`) when
 * the product was written but the settings were not — re-saving is idempotent;
 * `400 giving_mode_unsupported` for the retired `external` mode.
 * @param {{ mode: 'off'|'native', buttonLabel?: string|null, thankYouMessage?: string|null, givingMessage?: string|null,
 *   native?: { presets: number[], minAmount: number, currency: string, customAmountEnabled: boolean }, target?: 'draft' }} body
 */
function updateGiving(siteId, body) {
	return makeRequest({ method: 'put', path: `/sites/${siteId}/giving`, data: body })
}
/**
 * `{ enabled: false, reason }` when donations are off or the site has no donation product yet.
 * @param {{ days?: number }} [opts]
 * `count` / `total` / `last30` / `byMonth` are net of refunds; `gross` / `refunded` / `refundCount` are null on an older paywall.
 * @return {Promise<{ enabled: boolean, reason?: string, days?: number, currency?: string|null, count?: number, total?: number, gross?: number|null, refunded?: number|null, refundCount?: number|null, last30?: { count: number, total: number }, byMonth?: Array<{ month: string, count: number, total: number }>, lastDonationAt?: string|null }>}
 */
function getGivingSummary(siteId, { days } = {}) {
	return makeRequest({ path: `/sites/${siteId}/giving/summary`, params: days ? { days } : undefined })
}
/**
 * Paywall paginator passthrough (rows newest first, snake_case like transactions).
 * @param {{ page?: number, per_page?: number, q?: string, status?: string, from?: string, to?: string }} [params]
 * @return {Promise<{ enabled: boolean, reason?: string, data: object[], meta: { current_page: number, last_page: number, per_page: number, total: number } }>}
 */
function listDonations(siteId, params = {}) {
	return makeRequest({ path: `/sites/${siteId}/giving/donations`, params })
}
/**
 * Stripe refund of a succeeded / partially refunded donation; no `amount` refunds what is left.
 * 404 donation_not_found, 409 donation_not_refundable, 422 invalid_refund_amount, 502 stripe_error.
 * @param {{ amount?: number, reason?: 'requested_by_customer'|'duplicate'|'fraudulent' }} [body]
 * @return {Promise<{ donation: object, refund: { id: string, amount: number, currency: string, status: string, reason: string|null, created_at: string } }>}
 */
function refundDonation(siteId, donationId, body = {}) {
	return makeRequest({ method: 'post', path: `/sites/${siteId}/giving/donations/${encodeURIComponent(donationId)}/refund`, data: body })
}

async function makeRequest(reqConfig) {
	if (typeof reqConfig === 'string') {
		reqConfig = { path: reqConfig }
	}
	reqConfig.url = reqConfig.path

	let res
	try {
		res = await api.request(reqConfig)
	} catch (err) {
		const edata = _.get(err, 'response.data')
		// Runtime errors are { error, code, details }; castr/paywall errors are Boom.
		if (edata && edata.error && !edata.message) {
			const detailText = edata.details && typeof edata.details === 'object'
				? Object.entries(edata.details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.map((x) => (x && typeof x === 'object' ? x.title || x.id || '' : x)).join(', ') : v}`).join('; ')
				: ''
			const error = new RequestError({
				statusCode: _.get(err, 'response.status'),
				error: edata.error,
				message: detailText ? `${humanizeError(edata.error)} — ${detailText}` : humanizeError(edata.error, edata.code),
			})
			// Runtime sub-code (e.g. `conflict` / `featured_source`) and its structured details.
			error.reason = edata.code || edata.error || null
			error.details = edata.details && typeof edata.details === 'object' ? edata.details : null
			error.payload = edata
			throw error
		}
		// A non-JSON answer (e.g. the runtime's HTML 404 for a route it does not have yet) still carries its status.
		const status = _.get(err, 'response.status')
		const error = new RequestError(edata && typeof edata === 'object' ? { statusCode: status, ...edata } : { statusCode: status, error: err.message })
		// castr gates (stripe_not_connected, paywall_site_unconfigured, …) travel as Boom `reason`
		error.reason = edata?.reason || null
		error.details = edata?.stripe || null
		// castr's `withReason` copies extras (e.g. promos: `products`, `code`) to the Boom payload top level.
		error.payload = edata && typeof edata === 'object' ? edata : null
		throw error
	}
	return res.data
}

/** True when castr refused a monetizing write because Stripe is not connected. */
export function isStripeNotConnected(err) {
	return err?.reason === 'stripe_not_connected'
}

/** True when the site is not linked to the owner's Paywall account yet (castr 503). */
export function isPaywallSiteUnconfigured(err) {
	return err?.reason === 'paywall_site_unconfigured'
}

/** True when castr has no Cloudflare for SaaS zone/token for this environment (503). */
export function isCustomDomainsUnconfigured(err) {
	return err?.reason === 'custom_domains_unconfigured'
}

/** 403 from castr when the plan lacks `sites:custom-domain`. */
export function isCustomDomainsNotInPlan(err) {
	return err?.reason === 'custom_domains_not_in_plan'
}

/** 403 from castr when the site already has `sites:max-custom-domains` domains (`payload.max`). */
export function isCustomDomainsLimit(err) {
	return err?.reason === 'custom_domains_limit'
}

/** True when castr refused native giving because `SITES_DONATIONS_ENABLED` is off (409) or the broadcast template is off (409). */
export function isFeatureDisabled(err) {
	return err?.reason === 'donations_disabled' || err?.reason === 'templates_disabled'
}

/**
 * Slides blocking a hide: the runtime answers `409 featured_source` with
 * `details.slides: [{ id, title }]` when a content item is the source of a
 * featured slide (any of `code` / `error` / a bulk result `error` may carry the
 * sub-code). Returns the slides (possibly empty) or null when it is another error.
 * @return {Array<{ id?: number, title?: string }> | null}
 */
export function featuredSourceSlides(err) {
	if (!err) return null
	const code = err.reason || err.error || err.code
	const tagged = code === 'featured_source' || err.payload?.code === 'featured_source'
	if (!tagged) return null
	const slides = err.details?.slides ?? err.payload?.details?.slides
	return Array.isArray(slides) ? slides.filter((s) => s && typeof s === 'object') : []
}

/** True when the runtime behind castr does not serve `POST /content/bulk` yet (route missing → 404 / 405 / 501). */
export function isBulkUnsupported(err) {
	return [404, 405, 501].includes(Number(err?.code))
}

function humanizeError(code, sub) {
	const map = {
		validation_failed: 'Some fields are invalid',
		site_admin_auth_unconfigured: 'Site admin is not configured on the runtime yet',
		site_db_unconfigured: 'The site database is not configured yet',
		site_db_unreachable: 'The site database is unreachable',
		paywall_site_unconfigured: 'This site is not linked to your Paywall account yet',
		site_unavailable: 'The site is unavailable',
		not_found: 'Not found',
		conflict: 'Conflict',
	}
	const text = map[code] || String(code || 'Request failed').replace(/_/g, ' ')
	return sub ? `${text} (${sub})` : text
}
